import * as React from "react";
import { useState } from "react";
import { useSelector } from "react-redux";
import {
  Box, Typography,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
  useGetFieldAdminTeamOverviewQuery,
  useGetFieldAdminTeamInstallersQuery,
  useGetFieldAdminTeamConductFlagsQuery,
  useGetFieldAdminVehiclesQuery,
  useGetFieldAdminQEOversightQuery,
} from "./fieldAdminApi";
import { getUserName } from "../Authentication/authSlice";
import { CARD_BG, BORDER, AMBER } from "../../Global/colors";
import { FAPrimaryButton } from "./FAControls";
import { FONT_FAMILY, OK_GREEN_TEXT, WARN_YELLOW, BAD_RED_TEXT, teamLabel, installerLabel, initialsOf, formatDayTime, parseLocalDate } from "./designTokens";
import { FAPageState } from "./FAStates";
import { last30DaysRange } from "./HS/dateRangePresets";
import DateRangePill from "./DateRangePill";
import { FAPageShell } from "./FALayout";
import { EditTeamDialog, RowActionsMenu, InstallerDialog, RemoveConfirmDialog, ChangeVehicleDialog } from "./TeamDialogs";

// The design's pill reads "In · 06:38" / "Not in" / "Out", but AttendanceVerification
// has no time field at all — it's a verification layer, not a clock (the real
// clock-in/out lives in IMS's Attendance tab). So these are the four states that
// are genuinely backed: verified, flagged, seen-but-unverified, and no record today.
function attendanceMeta(attendance) {
  if (!attendance) return { label: "No record", bg: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.5)" };
  if (attendance.conduct_flag) {
    return { label: attendance.severity ? `Flagged · ${attendance.severity}` : "Flagged", bg: "rgba(251,191,36,0.12)", color: WARN_YELLOW };
  }
  if (attendance.fa_verified) return { label: "Verified", bg: "rgba(34,197,94,0.12)", color: "#4ADE80" };
  return { label: "Not verified", bg: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.5)" };
}

function AttendancePill({ attendance }) {
  const meta = attendanceMeta(attendance);
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex", alignItems: "center", gap: "5px", fontSize: "0.7rem", fontWeight: 600,
        px: "9px", py: "3px", borderRadius: "999px", letterSpacing: "0.02em", whiteSpace: "nowrap",
        backgroundColor: meta.bg, color: meta.color,
      }}
    >
      {meta.label}
    </Box>
  );
}

// Team's own .ov-tile — deliberately NOT Stock.js's StatTile (.stat-tile), which is
// 1.45rem amber. The design's .ov-value sets no colour at all, so it inherits white,
// at 1.05rem.
function OverviewTile({ label, value, sub }) {
  return (
    <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", px: "16px", py: "14px" }}>
      <Typography sx={{ fontSize: "0.62rem", fontWeight: 700, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.08em", mb: "5px" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color: "#fff", letterSpacing: "-0.015em", fontVariantNumeric: "tabular-nums", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.5)", mt: "2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {sub}
      </Typography>
    </Box>
  );
}

function SectionLink({ children, onClick }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        background: "none", border: "none", p: 0, cursor: "pointer", fontFamily: FONT_FAMILY,
        fontSize: "0.7rem", color: AMBER, fontWeight: 600, textTransform: "none", letterSpacing: "normal",
        display: "inline-flex", alignItems: "center", gap: "5px",
        "&:hover": { textDecoration: "underline" },
      }}
    >
      {children}
    </Box>
  );
}

export default function Team() {
  const navigate = useNavigate();
  const userName = useSelector(getUserName);
  const { data: overview, isSuccess: isOverviewSuccess, isError: isOverviewError, error: overviewError, refetch: refetchOverview } = useGetFieldAdminTeamOverviewQuery();
  const { data: installersData, isSuccess: isInstallersSuccess, isError: isInstallersError, error: installersError } = useGetFieldAdminTeamInstallersQuery();
  const { data: fleetData, isSuccess: isFleetSuccess } = useGetFieldAdminVehiclesQuery();
  // Reused as-is for the QE row's real "5 / 6 COC today" — same company-scoped feed
  // the QE oversight page itself reads, no new endpoint.
  const { data: qeData } = useGetFieldAdminQEOversightQuery();

  const [defaultFrom, defaultTo] = last30DaysRange();
  const [fromDate, setFromDate] = useState(defaultFrom);
  const [toDate, setToDate] = useState(defaultTo);
  const { data: flagsData, isSuccess: isFlagsSuccess } = useGetFieldAdminTeamConductFlagsQuery({ date_from: fromDate, date_to: toDate });

  const [editingInstaller, setEditingInstaller] = useState(null); // null closed, {} add, installer edit
  const [removeTarget, setRemoveTarget] = useState(null);
  const [rowMenu, setRowMenu] = useState(null); // { anchorEl, installer } or null
  const [vehicleModalOpen, setVehicleModalOpen] = useState(false);
  const [editTeamOpen, setEditTeamOpen] = useState(false);

  // TeamOverviewView chains 4 sequential IMS round-trips server-side (companies, field
  // areas, installers, target), so this is the slowest page in the section to resolve —
  // which is exactly why the design ships a skeleton for it rather than a blank screen.
  if (!isOverviewSuccess || !isInstallersSuccess) {
    return <FAPageState isError={isOverviewError || isInstallersError} error={overviewError || installersError} onRetry={refetchOverview} count={5} tiles={4} />;
  }

  const installers = installersData.installers;
  const assignedVehicle = isFleetSuccess
    ? fleetData.vehicles.find((v) => v.assigned_team_id === fleetData.team_id)
    : null;

  // One predicate for both halves of the tile. They used to disagree at install_target
  // === 0: the value used `!= null` and rendered "6 / 0", while the caption used
  // truthiness and read "No target set in IMS" directly underneath it.
  const hasInstallTarget = overview.install_target > 0;
  const installPct = hasInstallTarget
    ? `${((overview.installs_done / overview.install_target) * 100).toFixed(1)}%`
    : null;

  return (
    <>
      <FAPageShell>
        <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "16px", mb: "18px", flexWrap: "wrap" }}>
          <Box>
            <Typography sx={{ fontSize: "1.75rem", fontWeight: 700, color: "#fff", letterSpacing: "-0.025em" }}>
              {teamLabel(overview.team_name, overview.team_id)}
            </Typography>
            <Typography sx={{ fontSize: "0.86rem", color: "rgba(255,255,255,0.55)", mt: "3px" }}>
              FA <Box component="span" sx={{ color: AMBER, fontWeight: 600 }}>{userName || "—"}</Box>
              {overview.company_name ? ` · ${overview.company_name}` : ""}
              {overview.area_name ? ` · ${overview.area_name}` : ""}
            </Typography>
          </Box>
          {/* Team's own neutral .btn-outline — not Fleet's amber-tinted variant of the
              same class name. */}
          <Box
            component="button"
            type="button"
            onClick={() => setEditTeamOpen(true)}
            sx={{
              display: "inline-flex", alignItems: "center", gap: "7px", px: "14px", py: "9px",
              backgroundColor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "8px", color: "rgba(255,255,255,0.85)", fontFamily: FONT_FAMILY,
              fontSize: "0.82rem", fontWeight: 600, cursor: "pointer", textTransform: "none",
              transition: "all 0.15s",
              "&:hover": { borderColor: "rgba(253,185,64,0.4)", color: AMBER },
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: "14px" }} /> Edit team
          </Box>
        </Box>

        {/* Overview tiles */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" }, gap: "10px", mb: "24px" }}>
          <OverviewTile label="Installers" value={overview.installer_count} sub={`${overview.verified_count} checked in`} />
          <OverviewTile label="QE" value={overview.qe ? 1 : 0} sub={overview.qe ? overview.qe.name : "Not assigned"} />
          <OverviewTile
            label="Vehicle"
            value={assignedVehicle ? assignedVehicle.registration : "—"}
            sub={assignedVehicle ? assignedVehicle.make_model : "Unassigned"}
          />
          <OverviewTile
            label="Installs"
            value={
              hasInstallTarget ? (
                <>
                  {overview.installs_done}
                  <Box component="span" sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.4)", fontWeight: 500 }}> / {overview.install_target}</Box>
                </>
              ) : overview.installs_done
            }
            sub={installPct || "No target set in IMS"}
          />
        </Box>

        {/* Installers */}
        <Box sx={{ py: "6px", mb: "4px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, color: "rgba(255,255,255,0.4)" }}>
            Installers · {installers.length}
          </Typography>
          <SectionLink onClick={() => setEditingInstaller({})}>
            <AddIcon sx={{ fontSize: "12px" }} /> Add installer
          </SectionLink>
        </Box>

        {installers.length === 0 ? (
          <Typography sx={{ color: "rgba(255,255,255,0.4)", textAlign: "center", mt: "20px", mb: "22px", fontSize: "0.95rem" }}>
            No installers on this team yet — add an installer to build the team.
          </Typography>
        ) : (
          <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", overflow: "hidden", mb: "22px" }}>
            {installers.map((installer, index) => (
              <InstallerRow
                key={installer.id}
                installer={installer}
                isQE={overview.qe?.ims_installer_id === installer.id}
                isLast={index === installers.length - 1}
                onEdit={() => setEditingInstaller(installer)}
                onMenuOpen={(anchorEl) => setRowMenu({ anchorEl, installer })}
              />
            ))}
          </Box>
        )}

        {/* Qualified Electrician — IMS has no QE concept at all, so this reads the
            local TeamQEAssignment flag joined against the live roster above. */}
        <Typography sx={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, color: "rgba(255,255,255,0.4)", py: "6px", mb: "4px" }}>
          Qualified Electrician
        </Typography>
        {overview.qe ? (
          <QERow qe={overview.qe} qeData={qeData} onClick={() => navigate("/field-admin/more/qe-oversight/")} />
        ) : (
          /* team.html .empty-state is 0.55 alpha at 0.95rem, not 0.4 at 0.86rem. */
          <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", px: "20px", py: "40px", textAlign: "center", mb: "22px" }}>
            <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.95rem" }}>
              No QE flagged yet — edit an installer below and check "Assign Qualified Electrician".
            </Typography>
          </Box>
        )}

        {/* Vehicle — reuses Fleet's own vehicle data/mutation directly, same one
            source of truth, same one-vehicle-per-team invariant. */}
        <Box sx={{ py: "6px", mb: "4px", mt: "22px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
          <Typography sx={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, color: "rgba(255,255,255,0.4)" }}>
            Vehicle
          </Typography>
          <Box sx={{ display: "inline-flex", gap: "14px" }}>
            <SectionLink onClick={() => setVehicleModalOpen(true)}>
              {assignedVehicle ? "Change vehicle" : "Assign a vehicle"}
            </SectionLink>
            <SectionLink onClick={() => navigate("/field-admin/more/fleet/")}>Manage fleet →</SectionLink>
          </Box>
        </Box>
        {assignedVehicle ? (
          <Box
            onClick={() => navigate("/field-admin/hs/vehicle/")}
            sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", p: "14px 18px", display: "grid", gridTemplateColumns: "56px 1fr auto", alignItems: "center", gap: "16px", cursor: "pointer", transition: "border-color 0.15s", "&:hover": { borderColor: "rgba(253,185,64,0.3)" } }}
          >
            <Box sx={{ width: "56px", height: "56px", borderRadius: "12px", backgroundColor: "rgba(253,185,64,0.12)", color: AMBER, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <DirectionsCarFilledOutlinedIcon sx={{ fontSize: "26px" }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: "#fff", letterSpacing: "-0.01em" }}>
                {assignedVehicle.make_model} · {assignedVehicle.registration}
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.55)", mt: "2px" }}>
                {[
                  assignedVehicle.year,
                  assignedVehicle.fuel ? assignedVehicle.fuel.toLowerCase() : null,
                  assignedVehicle.odometer_km != null ? `${assignedVehicle.odometer_km.toLocaleString()} km` : null,
                  assignedVehicle.service_due_in_km != null ? `service due in ${assignedVehicle.service_due_in_km.toLocaleString()} km` : null,
                ].filter(Boolean).join(" · ")}
              </Typography>
              {assignedVehicle.last_inspection_at ? (() => {
                // The design's green ● is its .ok class on an all-pass inspection. Dev drew
                // it green unconditionally, so 8/12 pass still read as a clean vehicle.
                const allPass = assignedVehicle.last_inspection_items_total > 0
                  && assignedVehicle.last_inspection_items_checked === assignedVehicle.last_inspection_items_total;
                return (
                  <Typography sx={{ fontSize: "0.74rem", color: "rgba(255,255,255,0.5)", mt: "4px", display: "inline-flex", alignItems: "center", gap: "6px" }}>
                    <Box component="span" sx={{ color: allPass ? OK_GREEN_TEXT : WARN_YELLOW, fontWeight: 600 }}>●</Box>
                    Last inspection {formatDayTime(assignedVehicle.last_inspection_at, { todaySuffix: true })} · {allPass
                      ? "all items pass"
                      : `${assignedVehicle.last_inspection_items_checked}/${assignedVehicle.last_inspection_items_total} pass`}
                  </Typography>
                );
              })() : null}
            </Box>
            <ArrowForwardIosIcon sx={{ color: "rgba(255,255,255,0.35)", fontSize: "0.85rem" }} />
          </Box>
        ) : (
          <Box sx={{ backgroundColor: CARD_BG, border: "1px dashed rgba(255,255,255,0.14)", borderRadius: "12px", p: "20px 18px", textAlign: "center", color: "rgba(255,255,255,0.55)", fontSize: "0.86rem" }}>
            No vehicle assigned to this team.
            <Box sx={{ mt: "10px" }}>
              <FAPrimaryButton onClick={() => setVehicleModalOpen(true)}>Assign a vehicle</FAPrimaryButton>
            </Box>
          </Box>
        )}

        {/* Conduct flags — real AttendanceVerification rows, date-ranged. */}
        <Box sx={{ pt: "8px", pb: "12px", mt: "22px", display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, color: "rgba(255,255,255,0.4)" }}>
            Conduct flags
          </Typography>
          <DateRangePill
            scope="Conduct flags"
            from={fromDate}
            to={toDate}
            onApply={(from, to) => { setFromDate(from); setToDate(to); }}
          />
        </Box>
        {!isFlagsSuccess ? null : flagsData.flags.length === 0 ? (
          <Typography sx={{ color: "rgba(255,255,255,0.4)", textAlign: "center", mt: "12px", fontSize: "0.9rem" }}>
            No conduct flags in this range.
          </Typography>
        ) : (
          <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", p: "4px" }}>
            {flagsData.flags.map((flag, index) => (
              <Box
                key={`${flag.date}-${flag.installer_id}-${index}`}
                sx={{ display: "grid", gridTemplateColumns: "80px 1fr auto", gap: "14px", alignItems: "center", px: "14px", py: "11px", borderRadius: "8px", borderBottom: index === flagsData.flags.length - 1 ? "none" : "1px solid rgba(255,255,255,0.04)" }}
              >
                <Typography sx={{ fontSize: "0.74rem", color: "rgba(255,255,255,0.5)", fontVariantNumeric: "tabular-nums" }}>
                  {parseLocalDate(flag.date).toLocaleDateString(undefined, { day: "2-digit", month: "short" })}
                </Typography>
                <Typography sx={{ fontSize: "0.84rem", color: "rgba(255,255,255,0.85)", lineHeight: 1.4 }}>
                  <Box component="span" sx={{ fontWeight: 600, color: "#fff" }}>
                    {installerLabel(flag.installer_name, flag.installer_id)}
                  </Box> · {flag.note || "No note recorded"}
                </Typography>
                <Box
                  component="span"
                  sx={{
                    fontSize: "0.66rem", fontWeight: 700, px: "9px", py: "3px", borderRadius: "4px", textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap",
                    backgroundColor: flag.severity === "High" ? "rgba(239,68,68,0.14)" : flag.severity === "Med" ? "rgba(249,115,22,0.14)" : "rgba(251,191,36,0.12)",
                    color: flag.severity === "High" ? BAD_RED_TEXT : flag.severity === "Med" ? "#FB923C" : WARN_YELLOW,
                  }}
                >
                  {flag.severity || "Low"}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </FAPageShell>

      <RowActionsMenu rowMenu={rowMenu} onClose={() => setRowMenu(null)} onEdit={setEditingInstaller} onRemove={setRemoveTarget} />

      {editingInstaller ? (
        <InstallerDialog installer={editingInstaller} currentQE={overview.qe} onClose={() => setEditingInstaller(null)} />
      ) : null}
      {removeTarget ? (
        <RemoveConfirmDialog installer={removeTarget} onClose={() => setRemoveTarget(null)} />
      ) : null}
      {editTeamOpen ? (
        <EditTeamDialog overview={overview} onClose={() => setEditTeamOpen(false)} />
      ) : null}
      {vehicleModalOpen ? (
        <ChangeVehicleDialog
          vehicles={isFleetSuccess ? fleetData.vehicles : []}
          teamId={isFleetSuccess ? fleetData.team_id : null}
          teamName={overview.team_name}
          currentVehicleId={assignedVehicle?.id}
          onClose={() => setVehicleModalOpen(false)}
        />
      ) : null}
    </>
  );
}

function InstallerRow({ installer, isQE, isLast, onEdit, onMenuOpen }) {
  return (
    <Box
      onClick={onEdit}
      sx={{
        display: "flex", alignItems: "center", gap: "14px", px: "16px", py: "13px",
        borderBottom: isLast ? "none" : "1px solid rgba(255,255,255,0.04)", cursor: "pointer",
        "&:hover": { backgroundColor: "rgba(253,185,64,0.04)" },
      }}
    >
      <Box sx={{ width: "36px", height: "36px", borderRadius: "50%", backgroundColor: "rgba(255,255,255,0.06)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.78rem", fontWeight: 700, flexShrink: 0 }}>
        {initialsOf(installer.name)}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "7px" }}>
          <Typography sx={{ fontSize: "0.92rem", fontWeight: 600, color: "#fff" }}>{installer.name}</Typography>
          {isQE ? (
            <Box component="span" sx={{ fontSize: "0.58rem", fontWeight: 700, px: "6px", py: "2px", borderRadius: "4px", backgroundColor: "rgba(168,85,247,0.16)", color: "#C4B5FD", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              QE
            </Box>
          ) : null}
        </Box>
        <Typography sx={{ fontSize: "0.74rem", color: "rgba(255,255,255,0.5)", mt: "1px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {[installer.email_address, installer.number].filter(Boolean).join(" · ")}
        </Typography>
      </Box>
      {/* .person-stats — hidden below sm, exactly as the design hides it at <720px.
          No "Installs / wk": JobCard has no link to an IMS installer id, so that
          column would be invented. The attendance pill is real. */}
      <Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center", gap: "16px", flexShrink: 0 }}>
        <AttendancePill attendance={installer.attendance} />
      </Box>
      <IconButton
        size="small"
        onClick={(e) => { e.stopPropagation(); onMenuOpen(e.currentTarget); }}
        sx={{ color: "rgba(255,255,255,0.35)", flexShrink: 0, "&:hover": { color: AMBER, backgroundColor: "rgba(255,255,255,0.05)" } }}
      >
        <MoreVertIcon sx={{ fontSize: "1.05rem" }} />
      </IconButton>
    </Box>
  );
}

function QERow({ qe, qeData, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", p: "13px 16px", display: "flex", alignItems: "center", gap: "14px", cursor: "pointer", mb: "22px", "&:hover": { backgroundColor: "rgba(253,185,64,0.04)" } }}
    >
      <Box sx={{ width: "36px", height: "36px", borderRadius: "50%", backgroundColor: "rgba(168,85,247,0.18)", color: "#C4B5FD", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.78rem", fontWeight: 700, flexShrink: 0, letterSpacing: "-0.02em" }}>
        {initialsOf(qe.name)}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: "0.92rem", fontWeight: 600, color: "#fff" }}>{qe.name}</Typography>
        <Typography sx={{ fontSize: "0.74rem", color: "rgba(255,255,255,0.5)", mt: "1px" }}>
          QE · COC sign-off · read-only oversight from this app
        </Typography>
      </Box>
      <Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center", gap: "16px", flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
        {qeData ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "1px" }}>
            <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: "0.86rem" }}>
              {qeData.cocs_issued_today}/{qeData.installs_done_today}
            </Typography>
            <Typography sx={{ fontSize: "0.74rem", color: "rgba(255,255,255,0.55)" }}>COC today</Typography>
          </Box>
        ) : null}
        <AttendancePill attendance={qe.attendance} />
      </Box>
      <ArrowForwardIosIcon sx={{ color: "rgba(255,255,255,0.35)", fontSize: "0.85rem", flexShrink: 0 }} />
    </Box>
  );
}

